import React from 'react';
import { AlertTriangle, Zap, TrendingUp } from 'lucide-react';

/**
 * WalletMeter — credit balance widget with cap bar and low-balance warning.
 *
 * Props:
 *   dashboard  — DashboardResponse from /api/v1/billing/dashboard
 *   loading    — boolean
 */
function WalletMeter({ dashboard, loading }) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-8 bg-gray-200 rounded w-1/2 mb-2" />
        <div className="h-2 bg-gray-200 rounded w-full" />
      </div>
    );
  }

  if (!dashboard) return null;

  const balance = parseFloat(dashboard.credit_balance);
  const reserved = parseFloat(dashboard.reserved_credits);
  const available = parseFloat(dashboard.available_credits);
  const cap = dashboard.monthly_cap_credits ? parseFloat(dashboard.monthly_cap_credits) : null;
  const capPct = dashboard.cap_used_pct ? parseFloat(dashboard.cap_used_pct) : null;
  const monthSpendGbp = parseFloat(dashboard.month_spend_gbp || 0);
  const trailing30dCredits = parseFloat(dashboard.trailing_30d_credits || 0);

  // Display credits as £ (1 credit = £0.01)
  const toGbp = (credits) => (credits / 100).toFixed(2);

  const barPct = cap && cap > 0 ? Math.min(100, (balance / cap) * 100) : null;
  const barColor = dashboard.low_balance
    ? 'bg-red-500'
    : barPct !== null && barPct < 20
    ? 'bg-yellow-400'
    : 'bg-green-500';

  const tierLabel = {
    payg: 'Pay As You Go',
    pro: 'Pro',
    team: 'Team',
    enterprise: 'Enterprise',
  }[dashboard.tier?.toLowerCase()] || dashboard.tier;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Low balance warning */}
      {dashboard.low_balance && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>Low balance — less than 10% remaining relative to recent usage.</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Available Credits</p>
          <p className="text-3xl font-bold text-gray-900">£{toGbp(available)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{available.toLocaleString()} credits</p>
        </div>
        <div className="text-right">
          <span className="inline-block bg-blue-100 text-blue-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
            {tierLabel}
          </span>
          <p className="text-xs text-gray-400 mt-1">{dashboard.subscription_status}</p>
        </div>
      </div>

      {/* Cap progress bar */}
      {cap !== null && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Wallet balance</span>
            <span>Cap: £{toGbp(cap)}{capPct !== null ? ` (${capPct}% used)` : ''}</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${barColor}`}
              style={{ width: `${barPct ?? 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-100">
        <div>
          <p className="text-xs text-gray-500">Reserved</p>
          <p className="text-sm font-semibold text-gray-700">£{toGbp(reserved)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Month spend</p>
          <p className="text-sm font-semibold text-gray-700">£{monthSpendGbp.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">30d spend</p>
          <p className="text-sm font-semibold text-gray-700">£{toGbp(trailing30dCredits)}</p>
        </div>
      </div>

      {/* Platform fee badge for PAYG */}
      {dashboard.tier?.toLowerCase() === 'payg' && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
          <Zap className="w-3 h-3 inline mr-1" />
          Platform fee {dashboard.platform_fee_paid_month
            ? `paid for ${dashboard.platform_fee_paid_month}`
            : 'not yet collected this month'}
        </div>
      )}
    </div>
  );
}

export default WalletMeter;
